import { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "../../kit";
import type { InertTag } from "./schema";
import { useSectionInfo } from "./sectionContext";

/**
 * The audit chip. It used to be deliberately quiet — grey, 9px, the same weight
 * as a section sub-label — on the reasoning that a flagged row is a note to
 * whoever triages the inspector. In practice users read the surface as full of
 * controls that silently do nothing, so the chip now has to be *seen*: a small
 * amber badge, and the default word is "no effect" rather than the internal
 * jargon "inert". Rows that do something real but not what their label promises
 * pass their own label ("best-effort", "dev only", "duplicate").
 *
 * It stays small and non-blocking — the control keeps behaving exactly as it
 * did. Nothing is hidden or removed; the keep/delete triage in
 * docs/INERT-CONTROL-TRIAGE.md is a separate decision.
 */
export function InertChip({ tag }: { tag: InertTag }) {
  // 2026-09-29: a triage note, not something a jeweller should meet. The
  // chips read as the studio being broken ("DUPLICATE", "NO EFFECT" in amber
  // across the Advanced tabs), so production builds leave them out; dev and
  // the harnesses still see every one.
  if (!import.meta.env.DEV) return null;
  return (
    <span
      title={tag.reason}
      className="shrink-0 cursor-help rounded-[3px] border border-amber-700/40 bg-amber-500/10 px-1 text-[11px] font-semibold uppercase leading-[14px] tracking-[0.04em] text-amber-800"
    >
      {tag.label ?? "no effect"}
    </span>
  );
}

/**
 * Undo for controls that are not bound rows.
 *
 * The bound rows (RowRenderer) record through `history.ts`, which knows their
 * domain and pins material writes to the part that was selected. The ~80
 * bespoke controls (the light list, the full material editor, ground config…)
 * have no binding — only a `value` and an `onChange` — so they record here:
 * Undo calls the same `onChange` with the value it replaced. A section provides
 * `EditScope`; RowRenderer switches it off around the rows it records itself.
 */
export interface EditScopeValue {
  /** Shown in Undo's tooltip: "Undo Ground · Opacity". */
  section: string;
  record: (e: { label: string; key?: string; undo: () => Promise<void>; redo: () => Promise<void> }) => void;
}
export const EditScope = createContext<EditScopeValue | null>(null);
const RowLabelCtx = createContext<string | undefined>(undefined);

/**
 * Wrap `onChange` so the change joins the studio's history. `merge` coalesces
 * a gesture (one drag, one step) — its key is this control INSTANCE plus the
 * caller's gesture number, so two controls in one row never merge.
 */
function useRecorded<T>(value: T, onChange: (v: T, gesture?: number) => void, merge: boolean) {
  const scope = useContext(EditScope);
  const label = useContext(RowLabelCtx);
  const id = useId();
  const latest = useRef(onChange);
  latest.current = onChange;
  return useCallback(
    (nv: T, gesture = 0) => {
      onChange(nv, gesture);
      if (!scope || Object.is(nv, value)) return;
      const old = value;
      const apply = latest.current;
      scope.record({
        label: label ? `${scope.section} · ${label}` : scope.section,
        key: merge ? `${id}#${gesture}` : undefined,
        undo: async () => apply(old),
        redo: async () => apply(nv),
      });
    },
    [scope, label, id, value, onChange, merge],
  );
}

/** One row's extras, supplied by RowRenderer for bound rows. */
export interface RowExtras {
  /** Plain explanation, shown on hover/focus of the label. */
  help?: string;
  /** "0 – 4 · step 0.01" */
  range?: string;
  /** What it starts at, where that was verified ("0.05 (iJewel 0.015)"). */
  defaultText?: string;
  /** Shown after the value ("px", "°", "× model"). */
  unit?: string;
  pinned?: boolean;
  onPin?: () => void;
  /** Right-click menu. */
  menu?: { label: string; run: () => void; disabled?: boolean }[];
  /** Drag the label to change the value (numbers only). */
  scrub?: { value: number; min?: number; max?: number; step: number; onScrub: (v: number, gesture: number) => void };
  /** `data-row-path` for search jumps and the harness. */
  path?: string;
  /** Search terms to mark in the label. */
  mark?: string[];
}

/** Wrap every occurrence of the search terms in <mark>. */
export function Marked({ text, terms }: { text: string; terms?: string[] }) {
  const t = (terms ?? []).filter((x) => x.length > 1);
  if (!t.length || !text) return <>{text}</>;
  const re = new RegExp(`(${t.map((x) => x.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "ig");
  const parts = text.split(re);
  return (
    <>
      {parts.map((p, i) =>
        i % 2 ? (
          <mark key={i} className="rounded-[2px] bg-ed-active px-px text-ed-text">
            {p}
          </mark>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </>
  );
}

/** A menu that floats at a point (right-click) and closes on the next click or Escape. */
export function FloatingMenu({
  at,
  items,
  onClose,
}: {
  at: { x: number; y: number };
  items: { label: string; run: () => void; disabled?: boolean }[];
  onClose: () => void;
}) {
  useEffect(() => {
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onClose]);
  const w = 200;
  const left = Math.min(at.x, window.innerWidth - w - 8);
  const top = Math.min(at.y, window.innerHeight - items.length * 32 - 16);
  return createPortal(
    <div className="editor-theme fixed inset-0 z-[200]" onMouseDown={onClose} onContextMenu={(e) => { e.preventDefault(); onClose(); }}>
      <div
        role="menu"
        className="absolute overflow-hidden rounded-lg border border-ed-border bg-ed-panel py-1 shadow-[0_6px_20px_rgba(0,0,0,0.12)]"
        style={{ left, top, width: w }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {items.map((m) => (
          <button
            key={m.label}
            role="menuitem"
            type="button"
            disabled={m.disabled}
            onClick={() => { onClose(); m.run(); }}
            className="block w-full px-3 py-1.5 text-left text-[13px] text-ed-text hover:bg-ed-hover disabled:text-ed-text-3 disabled:hover:bg-transparent"
          >
            {m.label}
          </button>
        ))}
      </div>
    </div>,
    document.body,
  );
}

/**
 * The hover card for a row: what it does, its range, how to nudge it. It
 * opens to the LEFT of the label — the inspector sits on the right edge, so a
 * card to the right would be clipped by the window.
 */
function HelpCard({ anchor, title, extras }: { anchor: DOMRect; title: string; extras: RowExtras }) {
  const w = 260;
  const left = Math.max(8, anchor.left - w - 12);
  const top = Math.min(anchor.top - 4, window.innerHeight - 160);
  return createPortal(
    <div
      role="tooltip"
      className="editor-theme pointer-events-none fixed z-[210] rounded-lg border border-ed-border bg-ed-panel px-3 py-2.5 shadow-[0_6px_20px_rgba(0,0,0,0.12)]"
      style={{ left, top, width: w }}
    >
      <div className="text-[13px] font-semibold text-ed-text">{title}</div>
      {extras.help ? <p className="mt-1 text-[12px] leading-[1.45] text-ed-text-2">{extras.help}</p> : null}
      {extras.range || extras.defaultText ? (
        <p className="mt-1.5 text-[12px] tabular-nums text-ed-text-3">
          {[extras.range, extras.defaultText ? `Default ${extras.defaultText}` : null].filter(Boolean).join(" · ")}
        </p>
      ) : null}
      {extras.scrub ? <p className="mt-1 text-[11px] text-ed-text-3">Drag the name to adjust · Shift ×10 · Alt ×0.1</p> : null}
    </div>,
    document.body,
  );
}

/** Diagnostic rows ("Debug", "Debug Normals", "Debug only SSCS") are for
 *  developers: hidden, like the debug SECTIONS, unless "Show experimental". */
export function isDevLabel(label: string | undefined): boolean {
  return !!label && /\bdebug\b/i.test(label);
}

/** "flush" inside a custom block, whose own padding already insets its rows. */
export const RowInset = createContext<"normal" | "flush">("normal");

/** Inert kinds hidden unless "Show experimental" is on. "best-effort" rows
 *  do something real, so they stay. */
export function hiddenInert(tag: InertTag): boolean {
  const l = (tag.label ?? "no effect").toLowerCase();
  return l === "no effect" || l === "duplicate" || l === "dev only";
}

let scrubGesture = 1_000_000;

/**
 * Field names that reached the screen raw ("distanceScale", "focus",
 * "frustumSize" in the light and ground panels) read as words. Only a single
 * lower-camel token is touched; anything with a space or a capital first
 * letter is already a label.
 */
export function humanize(label: string): string {
  if (!/^[a-z][a-zA-Z0-9]*$/.test(label)) return label;
  const words = label.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();
  return words[0].toUpperCase() + words.slice(1);
}

/** A calm label-left / control-right row, the editor's atomic control unit. */
export function Row({
  label,
  hint,
  children,
  stacked,
  inert,
  extras,
  wide,
}: {
  label?: string;
  hint?: string;
  children: React.ReactNode;
  stacked?: boolean;
  inert?: InertTag;
  extras?: RowExtras;
  /** The label takes all the room the control leaves (switch rows — a 36px
   *  switch does not need 58% of the row, and long names wrapped to three lines). */
  wide?: boolean;
}) {
  const [tip, setTip] = useState<DOMRect | null>(null);
  const [menuAt, setMenuAt] = useState<{ x: number; y: number } | null>(null);
  const timer = useRef<number | null>(null);
  const labelRef = useRef<HTMLSpanElement>(null);
  const info = useSectionInfo();
  const inset = useContext(RowInset) === "flush" ? "px-0" : "px-3";
  const help = extras?.help ?? hint;
  const hasCard = !!(label && (help || extras?.range || extras?.defaultText));

  const openTip = () => {
    if (!hasCard || !labelRef.current) return;
    const r = labelRef.current.getBoundingClientRect();
    timer.current = window.setTimeout(() => setTip(r), 450);
  };
  const closeTip = () => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
    setTip(null);
  };
  useEffect(() => closeTip, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Drag-the-label scrub, the way every pro inspector does it: 4px per step,
  // Shift ×10, Alt ×0.1. A press that never moves 3px stays a click.
  const scrub = extras?.scrub;
  const onPointerDown = (e: React.PointerEvent) => {
    if (!scrub || e.button !== 0) return;
    const x0 = e.clientX;
    const v0 = scrub.value;
    const gesture = ++scrubGesture;
    let moved = false;
    const el = e.currentTarget as HTMLElement;
    const move = (ev: PointerEvent) => {
      const dx = ev.clientX - x0;
      if (!moved && Math.abs(dx) < 3) return;
      if (!moved) { moved = true; el.setPointerCapture?.(e.pointerId); closeTip(); }
      const mult = ev.shiftKey ? 10 : ev.altKey ? 0.1 : 1;
      let v = v0 + Math.round(dx / 4) * scrub.step * mult;
      if (scrub.min != null) v = Math.max(scrub.min, v);
      if (scrub.max != null) v = Math.min(scrub.max, v);
      const d = stepDecimals(scrub.step * mult);
      scrub.onScrub(Number(v.toFixed(d)), gesture);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  };

  // The star lives in the row's left gutter, outside the label column: inside
  // it, it took 24px of a 128px column and wrapped names like "Background
  // Intensity" onto two lines on every row, pinned or not.
  const pin = extras?.onPin ? (
    <button
      type="button"
      title={extras.pinned ? "Unpin" : "Pin to Pinned"}
      aria-label={extras.pinned ? `Unpin ${label ?? ""}` : `Pin ${label ?? ""}`}
      aria-pressed={!!extras.pinned}
      onClick={(e) => { e.stopPropagation(); extras.onPin?.(); }}
      className={`absolute top-1/2 flex h-5 w-3 -translate-y-1/2 items-center justify-center focus-visible:opacity-100 ${
        inset === "px-0" ? "-left-3" : "left-0"
      } ${extras.pinned ? "text-ed-accent opacity-100" : "text-ed-text-3 opacity-0 hover:text-ed-text group-hover/row:opacity-100"}`}
    >
      <Icon name="star" size={12} fill={extras.pinned} />
    </button>
  ) : null;

  const labelEl = label ? (
    <span
      className={`flex min-w-0 items-start gap-1 text-[12px] leading-[16px] text-ed-text-2 ${stacked ? "" : wide ? "flex-1" : "w-[128px] shrink-0"}`}
    >
      <span
        ref={labelRef}
        tabIndex={hasCard ? 0 : undefined}
        onMouseEnter={openTip}
        onMouseLeave={closeTip}
        onFocus={openTip}
        onBlur={closeTip}
        onPointerDown={onPointerDown}
        className={`min-w-0 break-words line-clamp-2 outline-none focus-visible:underline ${
          scrub ? "cursor-ew-resize select-none" : hasCard ? "cursor-help" : ""
        } ${hasCard ? "decoration-ed-line decoration-dotted underline-offset-2 hover:underline" : ""}`}
      >
        <Marked text={humanize(label)} terms={extras?.mark} />
      </span>
      {inert ? <InertChip tag={inert} /> : null}
    </span>
  ) : inert ? (
    <InertChip tag={inert} />
  ) : null;

  // A control the audit proved does nothing (or a second copy of one that
  // does) is not shown to a jeweller; "Show experimental" brings it back.
  if (((inert && hiddenInert(inert)) || isDevLabel(label)) && info && !info.showExperimental) return null;

  const onContextMenu = extras?.menu?.length
    ? (e: React.MouseEvent) => { e.preventDefault(); closeTip(); setMenuAt({ x: e.clientX, y: e.clientY }); }
    : undefined;

  const body = stacked ? (
    <div className={`group/row relative flex flex-col gap-1.5 ${inset} py-1.5`} data-row data-row-path={extras?.path} onContextMenu={onContextMenu}>
      {pin}
      {labelEl}
      {children}
    </div>
  ) : (
    <div className={`group/row relative flex min-h-[32px] items-center gap-2 ${inset} py-1`} data-row data-row-path={extras?.path} onContextMenu={onContextMenu}>
      {pin}
      {labelEl}
      <div className={`flex min-w-0 items-center justify-end gap-2 ${wide ? "shrink-0" : "flex-1"}`}>
        {children}
        {extras?.unit ? <span className="-ml-1 shrink-0 text-[11px] text-ed-text-3">{extras.unit}</span> : null}
      </div>
    </div>
  );

  return (
    <RowLabelCtx.Provider value={label}>
      {body}
      {tip && label ? <HelpCard anchor={tip} title={humanize(label)} extras={{ ...extras, help }} /> : null}
      {menuAt && extras?.menu ? <FloatingMenu at={menuAt} items={extras.menu} onClose={() => setMenuAt(null)} /> : null}
    </RowLabelCtx.Provider>
  );
}

function stepDecimals(step: number): number {
  return step >= 1 ? 0 : Math.min(6, Math.ceil(-Math.log10(step) - 1e-9));
}

export function CompactCheck({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label?: string }) {
  const change = useRecorded(checked, onChange, false);
  const rowLabel = useContext(RowLabelCtx);
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label ?? rowLabel}
      onClick={() => change(!checked)}
      className={`relative h-5 w-9 shrink-0 rounded-full border transition-colors ${
        checked ? "border-ed-accent bg-ed-accent" : "border-ed-line bg-ed-panel-2"
      }`}
    >
      <span
        className={`absolute top-[2px] h-[14px] w-[14px] rounded-full shadow-sm transition-all ${checked ? "bg-white" : "bg-ed-line"}`}
        style={{ left: checked ? "18px" : "2px" }}
      />
    </button>
  );
}

/** Parse what someone typed: "1.5", "1,5", "-.25". */
function parseTyped(s: string): number {
  return parseFloat(s.trim().replace(",", "."));
}

/**
 * A number field that commits on Enter or when it loses focus — not on every
 * keystroke, which sent 1 and then 15 when you typed "15" (two renders, and a
 * clamp to `min` on the way through could send something you never typed).
 * ↑/↓ step it (Shift ×10, Alt ×0.1) and commit at once; Escape puts it back.
 */
export function CompactNumber({
  value,
  min,
  max,
  step,
  onChange,
  width = 56,
}: {
  value: number;
  min?: number;
  max?: number;
  step?: number;
  onChange: (v: number, gesture?: number) => void;
  width?: number;
}) {
  const [local, setLocal] = useState(fmtNum(value));
  const focused = useRef(false);
  const change = useRecorded(value, onChange, true);
  const rowLabel = useContext(RowLabelCtx);
  const nudges = useRef(0);
  useEffect(() => {
    if (!focused.current) setLocal(fmtNum(value));
  }, [value]);
  const clamp = (v: number) => {
    if (min != null) v = Math.max(min, v);
    if (max != null) v = Math.min(max, v);
    return v;
  };
  const commit = () => {
    const v = parseTyped(local);
    if (Number.isNaN(v)) { setLocal(fmtNum(value)); return; }
    const c = clamp(v);
    setLocal(fmtNum(c));
    if (Math.abs(c - value) > 1e-12) change(c, ++nudges.current);
  };
  return (
    <input
      type="text"
      inputMode="decimal"
      data-own-focus
      aria-label={rowLabel}
      value={local}
      style={{ width }}
      onFocus={(e) => { focused.current = true; e.currentTarget.select(); }}
      onBlur={() => { focused.current = false; commit(); }}
      onChange={(e) => setLocal(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") { commit(); e.currentTarget.select(); }
        else if (e.key === "Escape") { setLocal(fmtNum(value)); e.currentTarget.blur(); }
        else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
          e.preventDefault();
          const base = parseTyped(local);
          const s = (step ?? 1) * (e.shiftKey ? 10 : e.altKey ? 0.1 : 1);
          const d = stepDecimals(s);
          const c = clamp(Number(((Number.isNaN(base) ? value : base) + (e.key === "ArrowUp" ? s : -s)).toFixed(d)));
          setLocal(fmtNum(c));
          // Held arrows are one gesture (one Undo step); a pause starts another.
          change(c, nudges.current);
        }
      }}
      className="rounded-md border border-ed-line bg-white px-1.5 py-[3px] text-right text-[12px] tabular-nums text-ed-text outline-none focus:border-ed-accent focus:ring-2 focus:ring-ed-accent/20"
    />
  );
}

function fmtNum(v: number): string {
  if (!Number.isFinite(v)) return v > 0 ? "∞" : "0";
  if (Number.isInteger(v)) return String(v);
  const a = Math.abs(v);
  if (a >= 100) return v.toFixed(1);
  if (a >= 1) return v.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
  return v.toFixed(4).replace(/0+$/, "").replace(/\.$/, "");
}

let sliderGesture = 0;

/**
 * Range + readout. A drag used to send one RPC per input event (hundreds per
 * second on a fast mouse); now the latest value is sent at most once a frame
 * and flushed on release. The frame timer has a timeout fallback because a
 * hidden tab never runs requestAnimationFrame.
 */
export function CompactSlider({
  value,
  min,
  max,
  step,
  onChange,
  format,
  unit,
}: {
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number, gesture?: number) => void;
  format?: (v: number) => string;
  unit?: string;
}) {
  const [local, setLocal] = useState(value);
  const dragging = useRef(false);
  const gesture = useRef(++sliderGesture);
  const pending = useRef<number | null>(null);
  const timers = useRef<{ raf: number; to: number } | null>(null);
  const change = useRecorded(value, onChange, true);
  const rowLabel = useContext(RowLabelCtx);
  useEffect(() => {
    if (!dragging.current && pending.current === null) setLocal(value);
  }, [value]);

  // Through a ref, so `flush` is stable and the unmount effect below runs on
  // unmount only — not on every value change mid-drag.
  const changeRef = useRef(change);
  changeRef.current = change;
  const flush = useCallback(() => {
    if (timers.current) {
      cancelAnimationFrame(timers.current.raf);
      window.clearTimeout(timers.current.to);
      timers.current = null;
    }
    const v = pending.current;
    pending.current = null;
    if (v !== null) changeRef.current(v, gesture.current);
  }, []);
  const schedule = (v: number) => {
    pending.current = v;
    if (timers.current) return;
    timers.current = { raf: requestAnimationFrame(flush), to: window.setTimeout(flush, 50) };
  };
  useEffect(() => () => flush(), [flush]);

  return (
    <>
      <input
        type="range"
        className="lux min-w-0 flex-1"
        aria-label={rowLabel}
        min={min}
        max={max}
        step={step}
        value={local}
        onPointerDown={() => { dragging.current = true; gesture.current = ++sliderGesture; }}
        onPointerUp={() => { dragging.current = false; flush(); }}
        onKeyUp={flush}
        onChange={(e) => {
          const v = parseFloat(e.target.value);
          setLocal(v);
          schedule(v);
        }}
      />
      {format ? (
        <span className="w-12 shrink-0 text-right text-[12px] tabular-nums text-ed-text-3">{format(local)}</span>
      ) : (
        <RecordOff>
          <CompactNumber value={local} min={min} max={max} step={step} onChange={(v, g) => { setLocal(v); change(v, g); }} width={unit ? 52 : 56} />
        </RecordOff>
      )}
    </>
  );
}

/** Switch recording off for controls whose owner records the change itself. */
export function RecordOff({ children }: { children: React.ReactNode }) {
  return <EditScope.Provider value={null}>{children}</EditScope.Provider>;
}

const HEX = /^#?([0-9a-f]{6})$/i;

export function CompactColor({
  value,
  onChange,
  clearable,
  onClear,
}: {
  value: string;
  onChange: (hex: string) => void;
  clearable?: boolean;
  onClear?: () => void;
}) {
  const change = useRecorded(value, onChange, true);
  const rowLabel = useContext(RowLabelCtx);
  const [text, setText] = useState(value);
  const focused = useRef(false);
  const picking = useRef(++sliderGesture);
  useEffect(() => {
    if (!focused.current) setText(value);
  }, [value]);
  const commitText = () => {
    const m = text.trim().match(HEX);
    if (!m) { setText(value); return; }
    const hex = `#${m[1].toLowerCase()}`;
    setText(hex);
    if (hex !== value.toLowerCase()) change(hex, ++sliderGesture);
  };
  return (
    <div className="flex items-center gap-1.5">
      <input
        type="color"
        aria-label={rowLabel ? `${rowLabel} colour` : "Colour"}
        value={value}
        onFocus={() => { picking.current = ++sliderGesture; }}
        onChange={(e) => change(e.target.value, picking.current)}
        className="h-6 w-8 cursor-pointer rounded-md border border-ed-line bg-white p-0.5"
      />
      <input
        type="text"
        aria-label={rowLabel ? `${rowLabel} hex` : "Hex colour"}
        data-own-focus
        value={text}
        spellCheck={false}
        onFocus={(e) => { focused.current = true; e.currentTarget.select(); }}
        onBlur={() => { focused.current = false; commitText(); }}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") commitText();
          else if (e.key === "Escape") { setText(value); e.currentTarget.blur(); }
        }}
        className="w-[72px] rounded-md border border-ed-line bg-white px-1.5 py-[3px] text-[12px] uppercase tabular-nums text-ed-text outline-none focus:border-ed-accent focus:ring-2 focus:ring-ed-accent/20"
      />
      {clearable && onClear ? (
        <button onClick={onClear} className="text-ed-text-3 hover:text-ed-text" title="Clear" aria-label="Clear colour">
          <Icon name="close" size={14} />
        </button>
      ) : null}
    </div>
  );
}

export function CompactSelect({
  value,
  options,
  onChange,
}: {
  value: string;
  options: { id: string; label: string }[];
  onChange: (v: string) => void;
}) {
  const change = useRecorded(value, onChange, false);
  const rowLabel = useContext(RowLabelCtx);
  return (
    <div className="relative min-w-0 flex-1">
      <select
        value={value}
        aria-label={rowLabel}
        data-own-focus
        onChange={(e) => change(e.target.value)}
        className="w-full appearance-none rounded-md border border-ed-line bg-white px-2 py-1 pr-6 text-[12px] text-ed-text outline-none focus:border-ed-accent focus:ring-2 focus:ring-ed-accent/20"
      >
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
      <Icon
        name="keyboard_arrow_down"
        size={15}
        className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-ed-text-3"
      />
    </div>
  );
}

export function CompactText({
  value,
  placeholder,
  onChange,
}: {
  value: string;
  placeholder?: string;
  onChange: (v: string) => void;
}) {
  const [local, setLocal] = useState(value);
  const change = useRecorded(value, onChange, false);
  const rowLabel = useContext(RowLabelCtx);
  useEffect(() => setLocal(value), [value]);
  return (
    <input
      type="text"
      value={local}
      aria-label={rowLabel}
      placeholder={placeholder}
      data-own-focus
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => {
        if (local !== value) change(local);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" && local !== value) change(local);
      }}
      className="min-w-0 flex-1 rounded-md border border-ed-line bg-white px-2 py-1 text-[12px] text-ed-text outline-none focus:border-ed-accent focus:ring-2 focus:ring-ed-accent/20"
    />
  );
}

/** Text input + an action button (URL loaders). */
/**
 * URL-style input + action button (load an HDR, a LUT, a model by URL…).
 *
 * `onSubmit` may return a promise; while it is pending the input and button are
 * disabled and the button shows a spinner, and the field is only cleared once
 * the work SUCCEEDS — so a failed load leaves the URL in place to retry or fix
 * rather than wiping it and looking like nothing happened. A rejection is caught
 * and surfaced inline instead of becoming an unhandled rejection.
 */
export function TextActionInput({
  placeholder,
  action,
  onSubmit,
}: {
  placeholder: string;
  action: string;
  onSubmit: (v: string) => void | Promise<unknown>;
}) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const submit = () => {
    const v = value.trim();
    if (!v || busy) return;
    setFailed(false);
    let r: void | Promise<unknown>;
    try {
      r = onSubmit(v);
    } catch {
      setFailed(true);
      return;
    }
    if (!r || typeof (r as Promise<unknown>).then !== "function") {
      setValue("");
      return;
    }
    setBusy(true);
    (r as Promise<unknown>).then(
      () => { setBusy(false); setValue(""); },
      () => { setBusy(false); setFailed(true); },
    );
  };

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
      <div className="flex min-w-0 flex-1 gap-1">
        <input
          type="text"
          value={value}
          disabled={busy}
          placeholder={placeholder}
          onChange={(e) => { setValue(e.target.value); if (failed) setFailed(false); }}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          className={`min-w-0 flex-1 rounded border bg-white px-2 py-1 text-[12px] text-ed-text outline-none disabled:opacity-60 ${
            failed ? "border-ed-red" : "border-ed-border focus:border-ed-accent"
          }`}
        />
        <button
          onClick={submit}
          disabled={busy}
          aria-busy={busy || undefined}
          className="shrink-0 rounded border border-ed-border bg-ed-panel-2 px-2 text-[12px] font-medium text-ed-text hover:border-ed-accent disabled:cursor-not-allowed disabled:opacity-60"
        >
          {busy ? <Icon name="progress_activity" size={13} className="animate-spin" /> : action}
        </button>
      </div>
      {failed ? <span className="text-[11px] text-ed-red">Failed to load — check the URL.</span> : null}
    </div>
  );
}

/**
 * Small inspector button.
 *
 * `busy` shows a spinner in place of the icon AND disables the button; `disabled`
 * does the latter alone. Before these existed, callers that tracked an in-flight
 * flag could only swap the label text — the button stayed fully clickable, so a
 * slow export or texture load could be fired again on every click.
 */
export function MiniBtn({
  children,
  onClick,
  active,
  primary,
  icon,
  disabled,
  busy,
  inert,
}: {
  children: React.ReactNode;
  onClick: () => void;
  active?: boolean;
  primary?: boolean;
  icon?: string;
  disabled?: boolean;
  busy?: boolean;
  inert?: InertTag;
}) {
  const off = disabled || busy;
  const cls = off
    ? "cursor-not-allowed border-ed-border bg-ed-panel-2 text-ed-text-3 opacity-60"
    : primary
      ? "border-ed-text bg-ed-text text-white hover:bg-black"
      : active
        ? "border-ed-accent bg-ed-active text-ed-text"
        : "border-ed-border bg-white text-ed-text hover:border-ed-accent";
  const btn = (
    <button
      onClick={off ? undefined : onClick}
      disabled={off}
      aria-busy={busy || undefined}
      className={`inline-flex items-center justify-center gap-1 rounded border px-2 py-1 text-[12px] font-medium transition-colors ${cls}`}
    >
      {busy ? (
        <Icon name="progress_activity" size={14} className="animate-spin" />
      ) : icon ? (
        <Icon name={icon} size={14} />
      ) : null}
      {children}
    </button>
  );
  // The chip sits outside the button: a `title` on a disabled button is not
  // reliably shown, and these placeholders are exactly the disabled ones.
  return inert ? (
    <span className="inline-flex items-center gap-1">
      {btn}
      <InertChip tag={inert} />
    </span>
  ) : (
    btn
  );
}
